import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { type MenuOption } from '@saviynt/design-system';
import { type IntlShape, useIntl } from 'react-intl';

import { useAgentState } from '../../../shared/app/contexts/AgentContext';
import { type AgentRow } from '../component';
import { getFilterConfig } from '../utils/utilManagepage';

const MANAGE_SORT_SESSION_KEY = 'agent:manage:sort';

export interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

export interface FilterState {
  label: string;
  value: string;
  options: MenuOption[];
  isExpanded?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  hasMoreOptions?: boolean;
  searchText?: string;
}

/** Complete state shape for the manage page, including search, sort, and filters. */
interface ManagePageState {
  isGlobalLoading: boolean;
  searchText: string;
  sort: SortState;
  isFilterBar: boolean;
  filters: FilterState[];
  activeFilters: Record<string, string[]>;
  refreshAgent: Record<string, Partial<AgentRow>> | null;
}

/**
 * Builds the initial manage page state with localized filter configuration.
 * @param intl - The react-intl `IntlShape` instance used to localize filter labels.
 * @returns The default {@link ManagePageState}.
 */
const getStoredSort = (): SortState => {
  try {
    const stored = sessionStorage.getItem(MANAGE_SORT_SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as SortState;
      if (parsed?.field && parsed?.direction) {
        return parsed;
      }
    }
  } catch {
    // Ignore corrupted or unavailable session storage
  }
  return { field: 'name', direction: 'asc' };
};

const getInitialState = (intl: IntlShape): ManagePageState => ({
  isGlobalLoading: false,
  searchText: '',
  sort: getStoredSort(),
  isFilterBar: false,
  filters: getFilterConfig(intl),
  activeFilters: {},
  refreshAgent: null,
});

/**
 * Action type for the manage page reducer.
 * Accepts either a partial state object or a function that receives the current state and returns a partial update.
 */
type ManagePageAction =
  | Partial<ManagePageState>
  | ((state: ManagePageState) => Partial<ManagePageState>);

/**
 * Reducer for the manage page state. Merges partial updates into the current state.
 * @param state - The current manage page state.
 * @param action - A partial state object or a function producing one.
 * @returns The updated {@link ManagePageState}.
 */
const reducer = (
  state: ManagePageState,
  action: ManagePageAction
): ManagePageState => {
  const patch = typeof action === 'function' ? action(state) : action;

  return {
    ...state,
    ...patch,
  };
};

/**
 * React context providing the manage page state and dispatch.
 * Consumers should use {@link useManagePage}, {@link useManagePageState}, or {@link useManagePageDispatch} instead of accessing this directly.
 */
const ManagePageContext = createContext<
  | {
      state: ManagePageState;
      dispatch: React.Dispatch<ManagePageAction>;
    }
  | undefined
>(undefined);

/**
 * Provider component that initializes and manages the manage page state.
 * Page-level alert banners are handled by the app-level `AlertBannerProvider`
 * via the `useAlertBanner()` hook — this provider no longer owns banner state.
 * @param props.children - Child components that can consume the manage page context.
 */
export function ManagePageProvider({ children }: { children: ReactNode }) {
  // Hooks
  const intl = useIntl();
  const initialState: ManagePageState = getInitialState(intl);

  // States
  const [state, dispatch] = useReducer(reducer, initialState);

  // Effects
  useEffect(() => {
    try {
      sessionStorage.setItem(
        MANAGE_SORT_SESSION_KEY,
        JSON.stringify(state.sort)
      );
    } catch {
      // Ignore session storage write failures
    }
  }, [state.sort]);

  // Constants
  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <ManagePageContext.Provider value={value}>
      {children}
    </ManagePageContext.Provider>
  );
}

/**
 * Returns the full manage page context (state and dispatch).
 * Must be called within a {@link ManagePageProvider}.
 * @throws {Error} If used outside of a `ManagePageProvider`.
 */
export function useManagePage() {
  const context = useContext(ManagePageContext);

  if (context === undefined) {
    throw new Error('useManagePage must be used within a ManagePageProvider');
  }

  return context;
}

/**
 * Convenience hook that returns the manage page state merged with global agent state (`isMenuPinned`, `globalDateFormat`).
 * Must be called within both a {@link ManagePageProvider} and an `AgentProvider`.
 */
export function useManagePageState() {
  const { state } = useManagePage();
  const { isMenuPinned, globalDateFormat } = useAgentState();

  return {
    ...state,
    isMenuPinned,
    globalDateFormat,
  };
}

/**
 * Returns only the dispatch function from the manage page context.
 * Useful when a component needs to update state but doesn't need to read it.
 * Must be called within a {@link ManagePageProvider}.
 * @throws {Error} If used outside of a `ManagePageProvider`.
 */
export function useManagePageDispatch() {
  const context = useContext(ManagePageContext);

  if (context === undefined) {
    throw new Error(
      'useManagePageDispatch must be used within a ManagePageProvider'
    );
  }

  return context.dispatch;
}
